import { OverlayContainer, OverlayRef } from '@angular/cdk/overlay';
import { ComponentRef, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AlertController, ModalController } from '@ionic/angular';
import { Subscription, Observable, combineLatest, BehaviorSubject, firstValueFrom } from 'rxjs';
import { take, map, filter, distinctUntilChanged } from 'rxjs/operators';
import { GameStateId } from 'src/app/core/state/game/game.state';
import { SplitScreenService } from '../../splitScreen.service';
import { NavigationEnd, Router, RouterEvent, Event } from '@angular/router';
import { SopoDialogRef } from 'src/app/sopo-popup/interfaces/dialog';
import { SopoAlertComponent } from 'src/app/sopo-popup/components/sopo-alert/sopo-alert.component';

// override angular overlay creation
@Injectable()
export class GameOverlayContainer extends OverlayContainer {
    protected _containerElementInGame: HTMLElement;

    protected _createContainer(): void {
        const container: HTMLDivElement = document.createElement("div");
        container.classList.add("game-overlay-container");
        container.style.zIndex = '2000';

        const element: Element | null = document.querySelector(".app-game-content");
        if (element !== null) {
            element.appendChild(container);
            this._containerElementInGame = container;
        }
    }
    getContainerElement(): HTMLElement {
        if (
        !(
            this._containerElementInGame &&
            document.body.contains(this._containerElementInGame)
        )
        ) {
        this._createContainer();
        }

        return this._containerElementInGame;
    }
}

// override ionic alert controller creation
@Injectable()
export class GameAlertController extends AlertController {
    create(opts?): Promise<any> {
        /* tslint:disable-next-line */
        if (typeof customElements !== "undefined") {
            return customElements.whenDefined("ion-alert").then(() => {
                const element = document.createElement("ion-alert");
                element.classList.add("overlay-hidden");
                Object.assign(element, opts);
                // append the overlay element to the document body
                const gameElement: Element | null = document.querySelector(".app-game-content");
                if (gameElement !== null) {
                gameElement.appendChild(element);
                }
                return element.componentOnReady() as any;
            });
        }
        return Promise.resolve() as any;
    }
}

@Injectable()
export class GameModalController extends ModalController {
    create(opts?): Promise<HTMLIonModalElement> {
       return this.createModal({
           ...opts,
           delegate: this['angularDelegate'].create(this['resolver'], this['injector'])
       })
    }

    createModal(opts?) {
        /* tslint:disable-next-line */
        if (typeof customElements !== "undefined") {
            return customElements.whenDefined("ion-modal").then(() => {
                const element = document.createElement("ion-modal");
                element.classList.add("overlay-hidden");
                element.style.zIndex = '999';
                Object.assign(element, opts);
                // append the overlay element to the document body
                const gameElement: Element | null = document.querySelector(".app-game-content");
                if (gameElement !== null) {
                gameElement.appendChild(element);
                }
                return element.componentOnReady() as any;
            });
        }
        return Promise.resolve() as any;
    }
}

@Injectable({
    providedIn:'root',
})
export class GameDialogService {

    private openDialogs$ = new BehaviorSubject<{dialogRef: MatDialogRef<any>, gameStateId: GameStateId}[]>([]);
    private openAlerts$ = new BehaviorSubject<{alert: HTMLIonAlertElement | HTMLIonModalElement, gameStateId: GameStateId}[]>([]);
    private notInGameSubscription: Subscription;

    constructor (
        private sss: SplitScreenService,
        private router: Router,
    ){
        this.notInGameSubscription = this.router.events.pipe(
                filter((event: Event | RouterEvent) => event instanceof NavigationEnd),
                map((event: NavigationEnd) => !event.urlAfterRedirects.includes('game')),
                distinctUntilChanged(),
                filter(value => value),
            ).subscribe(() => {
                this.closeDialogsOnGamePage();
            }
        )
    }

    async getBackDropClass(gameStateId: GameStateId) : Promise<string> {
        return firstValueFrom(this.getBackDropClassObs(gameStateId));
    }

    changeMatDialogClassOnViewChange(gameStateId: GameStateId, dialogRef: MatDialogRef<any> , alwaysFullPage: boolean = false){
        let dialogSub: Subscription;
        dialogRef.afterOpened().pipe(take(1)).subscribe(() => {
            this.openDialogs$.next([...this.openDialogs$.value,{dialogRef, gameStateId}])
            if(alwaysFullPage) return;
            const overlayRef = dialogRef['_ref']['overlayRef'] as OverlayRef;
            const dialogElementBackdrop = overlayRef.backdropElement;
            dialogSub = this.getBackDropClassObs(gameStateId).subscribe(backdropClass => {
                const previousClass = dialogRef._containerInstance._config.backdropClass as string;
                if (dialogElementBackdrop && previousClass){
                    dialogElementBackdrop.classList.replace(previousClass, backdropClass);
                    dialogRef._containerInstance._config.backdropClass = backdropClass;
                }
            });
    
        })
        dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
            const dialogs = this.openDialogs$.value;
            const dialogToRemove = dialogs.findIndex(dialog => dialog.dialogRef.id == dialogRef.id);
            if (dialogToRemove >= 0){
                dialogs.splice(dialogToRemove, 1);
                this.openDialogs$.next(dialogs);
            }
            if (dialogSub){
                dialogSub.unsubscribe();
            }
        });
    }

    changeAlertClassOnViewChange(gameStateId: GameStateId, alert: HTMLIonAlertElement | HTMLIonModalElement, baseClasses?: string[]){
        if (!alert.id) {
            alert.id = `alert_${this.openAlerts$.value.length}`;
        }
        this.openAlerts$.next([...this.openAlerts$.value, {alert, gameStateId}])
        const alertSub = this.getBackDropClassObs(gameStateId).subscribe(backdropClass => {
            if (baseClasses) {
                let classes: string = '';
                baseClasses.forEach(cssClass => {
                    classes += `${cssClass} `;
                });
                classes += backdropClass;
                alert.cssClass = classes;
            } else {
                alert.cssClass = backdropClass;
            }
        });
        alert.onDidDismiss().then(() => {
            const alerts = this.openAlerts$.value;
            const alertToRemove = alerts.findIndex(dialog => dialog.alert.id == alert.id);
            if (alertToRemove >= 0){
                alerts.splice(alertToRemove, 1);
                this.openAlerts$.next(alerts);
            }
            if (alertSub){
                alertSub.unsubscribe();
            }
        })
    }

    private getBackDropClassObs(gameStateId: GameStateId) : Observable<string> {
        return combineLatest([this.sss.gamesList$, this.sss.gridView$, this.sss.selectedGame$]).pipe(
            map( ([gamesList, isGridView, selectedGame]) => {
                if (isGridView){
                    const index = gamesList.findIndex( game => game.gameStateId == gameStateId);
                    return `split-backdrop${gamesList.length}-${index}`;
                } else {
                    if (selectedGame == gameStateId){
                        return 'cdk-overlay-dark-backdrop';
                    }
                    else {
                        return 'split-backdrop-hidden'
                    }
                }
            })
        );
    }

    public getOpenDialogsPerGame(gameStateId: GameStateId) {
        return this.openDialogs$.value.filter(dialog => dialog.gameStateId == gameStateId);
    }

    public getOpenAlertsPerGame(gameStateId: GameStateId) {
        return this.openAlerts$.value.filter(dialog => dialog.gameStateId == gameStateId);
    }

    public getAlertsAndDialogsCount$(gameStateId: GameStateId): Observable<{dialogs: number, alerts: number}> {
        return combineLatest([this.openDialogs$.asObservable(),this.openAlerts$.asObservable()]).pipe(
            map(([dialogs, alerts]) => {
                const count = {dialogs: 0, alerts: 0};
                if (dialogs.length) count.dialogs = dialogs.filter(dialog => dialog.gameStateId == gameStateId).length;
                if (alerts.length) count.alerts = alerts.filter(alert => alert.gameStateId == gameStateId).length;
                return count;
            })
        );
    }

    closeDialogsOnGamePage() {
        const dialogsToClose = this.getOpenDialogsPerGame('');
        if (dialogsToClose.length === 0) return;
        for (const dialogToClose of dialogsToClose) {
            dialogToClose.dialogRef.close();
        }
    }

    changeSopoDialogClassOnViewChange(gameStateId: GameStateId, dialogRef: SopoDialogRef): void {
        const dialogSub = this.getBackDropClassObs(gameStateId).subscribe(backdropClass => {
            dialogRef.dialogComponentRef.instance.backdropClass = `sopo-${ backdropClass }`;
        });

        dialogRef.afterClosed.pipe(take(1)).subscribe(() => {
            if (dialogSub) {
                dialogSub.unsubscribe();
            }
        });
    }

    changeSopoAlertClassOnViewChange(gameStateId: GameStateId, alertRef: ComponentRef<SopoAlertComponent>): void {
        const alertSub = this.getBackDropClassObs(gameStateId).subscribe(backdropClass => {
            alertRef.instance.backdropClass = `sopo-${ backdropClass }`;
        });

        alertRef.instance.getAlertClosed().pipe(take(1)).subscribe(() => {
            if (alertSub) {
                alertSub.unsubscribe();
            }
        });
    }

    ngOnDestroy(): void {
        if(this.notInGameSubscription) this.notInGameSubscription.unsubscribe();
    }
}
